import { generatePath } from 'react-router-dom'
import { Transform } from 'prosemirror-transform'

import { HandbookV2, HandbookIndexArticle, Workspace, HandbookItemType, HandbookItem } from 'types'
import { FOLDER_PATH, HANDBOOK_PATH } from 'paths'
import { schema } from 'prose-core'
import { REACT_APP_FRONTEND_HOST, REACT_APP_FRONTEND_PROTOCOL, REACT_APP_FRONTEND_URL } from 'env'
import store from 'redux/store'
import { getEntityIdFromUrl } from 'utils/urlStoreUtils'
import { isCertified } from 'api/clients/WorkspaceDomainClientUtils'
import { getLocation } from 'hooks/useHistory'
import { HandbooksStore } from 'stores/HandbooksStore'
import { frontendOrigin } from 'utils/frontendOrigin'

export const handbookLink = (
  handbook: Pick<HandbookV2, 'seo_string' | 'custom_path'> | string,
  doc?: string,
  folder?: boolean,
) => {
  const path = folder ? FOLDER_PATH : HANDBOOK_PATH

  if (typeof handbook === 'string') {
    return generatePath(path, { folderSlug: handbook, doc })
  }

  if ('custom_path' in handbook && handbook.custom_path && getLocation().pathname.includes(handbook.custom_path)) {
    return generatePath('/:custom_path/:doc?', { custom_path: handbook.custom_path, doc })
  }

  return generatePath(path, { folderSlug: handbook.seo_string, doc })
}

export const handbookItemLink = (handbook: HandbookV2, item: HandbookItem) => {
  const slug = item.folder ? `folder_${item.folder?.key}` : item.document?.seo_string
  return handbookLink(handbook, slug, !!handbook.folder)
}

export const handbookFullUrl = (handbook: HandbookV2, doc?: string) => {
  const path = handbookLink(handbook, doc, !!handbook.folder)
  return `${frontendOrigin()}${path}`
}

/**
 * Default text for handbooks' cover docs
 */
export const defaultCoverDocSteps = (name: string) => {
  const doc = schema.node('doc', {}, [schema.node('paragraph', {}, [])])
  const paragraphs = [
    'Welcome to your new handbook! Handbooks are a great way to share a collection of your documents with the people in your workspace or with the public.',
    'The best part is that anyone who can view this handbook can also suggest changes to its documents.',
  ]

  const nodes = [
    schema.node('heading', { level: 1 }, [schema.text(name)]),
    ...paragraphs.map((p) => schema.node('paragraph', {}, [schema.text(p)])),
  ]

  return new Transform(doc).replaceWith(0, 2, nodes).steps
}

/**
 * Wraps Handbook 2.0 into HandbookArticle (1.0) interface for compatibility reasons
 * Currently its main use is the PublishHandbook component
 */
export const asHandbookV1 = (hb: HandbookV2): HandbookIndexArticle => ({
  id: hb.key,
  article_link: handbookLink(hb),
  custom_handbook_path: hb.custom_path,
  handbook_published_at: hb.published_at,
  public_handbook_image_url: hb.image_url ?? undefined,
  title: hb.name,
  url: handbookLink(hb),
  slug: hb.seo_string,
  header_emoji_color: null,
  v2: true,
})

export const syncHandbookStore = (handbook: HandbookV2, store: HandbooksStore) => {
  store.add(handbook)
  store.setCurrentHandbook(handbook.key)
}

/**
 * Gets the handbook's public url
 */
export const getHandbookPublicUrl = (workspace?: Workspace, handbook?: HandbookIndexArticle) => {
  if (!workspace || !handbook) return

  const { workspace_domain } = workspace

  if (!workspace_domain || !isCertified(workspace_domain)) {
    return `${REACT_APP_FRONTEND_URL}${handbook.article_link}?public=true`
  }

  const domainName = workspace_domain.subdomain
    ? `${workspace_domain.name}.${REACT_APP_FRONTEND_HOST}`
    : workspace_domain.name

  if (!!handbook.custom_handbook_path) {
    return `${REACT_APP_FRONTEND_PROTOCOL}//${domainName}/${handbook.custom_handbook_path}?public=true`
  }

  return `${REACT_APP_FRONTEND_PROTOCOL}//${domainName}${handbook.article_link}?public=true`
}

/**
 * Returns a handbook-doc URL if the URL param is for a document that is part of the current handbook
 */
export const wrapURLWithHandbook = (url: string) => {
  const { handbook } = store.getState()

  // check if user is currently in a handbook
  if (!handbook) return null

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const docKeys = handbook.items.filter((i) => i.document).map((i) => i.document!.key)
  const key = getEntityIdFromUrl(url)
  if (!key || !docKeys.includes(key)) return null

  const newURL = new URL(url)
  newURL.pathname = handbookLink(handbook, key)
  return newURL.toString()
}

export const stringType = (type: HandbookItemType) => {
  const translations = new Map<string, string>(
    Object.entries({
      [HandbookItemType.Document]: 'doc',
      [HandbookItemType.NestedHandbook]: 'folder',
      [HandbookItemType.Section]: 'section',
      [HandbookItemType.Link]: 'link',
    }),
  )
  return translations.get(type) || type
}

export const isLegacyHandbook = (handbook: HandbookV2) => {
  return !handbook.folder
}
